// Вывод и выбор устройства.
//
// Путей два, и это не перестраховка: `AudioContext.setSinkId` есть только в
// Chromium — в Firefox его по-прежнему нет. Универсальный путь идёт через
// `MediaStreamAudioDestinationNode` → `<audio>` → `HTMLMediaElement.setSinkId`,
// он работает везде, но прогоняет звук через медиа-элемент и добавляет
// собственную буферизацию. Какой путь выбран — обязательно показывать в
// интерфейсе: это первое, что захочется знать при жалобе на задержку.

/** Глубина jitter-буфера по умолчанию, мс. */
export const DEFAULT_JITTER_MS = 100;
export const DEFAULT_OUTPUT_RATE = 48_000;

export type SinkPath = "context" | "element";

export interface Playback {
  node: AudioWorkletNode;
  path: SinkPath;
  /** Переключить устройство на лету. Пустая строка — системное по умолчанию. */
  setSink(deviceId: string): Promise<void>;
  stop(): void;
}

export interface PlaybackOptions {
  context: AudioContext;
  deviceId: string;
  jitterMs: number;
  buildTag: string;
  /** Элемент из разметки: play() уже вызван внутри жеста. */
  element: HTMLAudioElement;
  /** Выбранный выход не удалось применить — звук идёт в системный. */
  onSinkProblem?: (text: string) => void;
}

/**
 * Сколько ждать переключения устройства вывода.
 *
 * `setSinkId` умеет не отвечать вовсе: устройство могло исчезнуть, быть занято
 * монопольно или просто оказаться из прошлой сессии, где идентификаторы были
 * другими. Ждать этого бесконечно нельзя — на это уходит весь запуск.
 */
const SINK_TIMEOUT_MS = 5_000;

async function withTimeout(work: Promise<void>, what: string): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const limit = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${what}: нет ответа`)), SINK_TIMEOUT_MS);
  });
  try {
    await Promise.race([work, limit]);
  } finally {
    clearTimeout(timer);
  }
}

/** Умеет ли браузер переключать выход самому контексту. */
function contextSinkSupported(): boolean {
  return typeof AudioContext !== "undefined" && "setSinkId" in AudioContext.prototype;
}

/** Создать контекст вывода. Синхронно — вызывать в обработчике жеста. */
export function createPlaybackContext(): AudioContext {
  return new AudioContext({ sampleRate: DEFAULT_OUTPUT_RATE, latencyHint: "interactive" });
}

export async function startPlayback(options: PlaybackOptions): Promise<Playback> {
  const { context, jitterMs, element } = options;

  await context.audioWorklet.addModule(`playback-processor.js?v=${options.buildTag}`);

  // Глубина буфера в сэмплах: заданные миллисекунды на частоте контекста.
  const prefill = Math.round((context.sampleRate * jitterMs) / 1000);
  const node = new AudioWorkletNode(context, "playback", {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [1],
    processorOptions: { prefill, capacity: context.sampleRate },
  });

  if (contextSinkSupported()) {
    node.connect(context.destination);
    const sink = context as AudioContext & { setSinkId(id: string): Promise<void> };

    const playback: Playback = {
      node,
      path: "context",
      async setSink(deviceId) {
        // Пустая строка означает «системное по умолчанию»; лишний вызов
        // setSinkId('') не везде безобиден, поэтому просто не зовём.
        if (deviceId !== "") {
          await withTimeout(sink.setSinkId(deviceId), "переключение выхода");
        }
      },
      stop() {
        node.disconnect();
      },
    };

    // Выбор устройства — удобство, а не условие работы, поэтому запуск его не
    // ждёт. Иначе зависший `setSinkId` останавливает всё: граф уже собран,
    // звук пошёл бы, но пользователь смотрит на «готовлю вывод звука…».
    // Не получилось — играем в системный выход и говорим об этом.
    void playback.setSink(options.deviceId).catch(() => {
      options.onSinkProblem?.("Выбранный выход недоступен, играю в системный.");
    });
    return playback;
  }

  // Firefox: звук уходит в медиа-поток, устройство выбирает элемент.
  const destination = context.createMediaStreamDestination();
  node.connect(destination);
  element.srcObject = destination.stream;

  const media = element as HTMLAudioElement & { setSinkId?(id: string): Promise<void> };
  const playback: Playback = {
    node,
    path: "element",
    async setSink(deviceId) {
      if (deviceId !== "" && typeof media.setSinkId === "function") {
        await withTimeout(media.setSinkId(deviceId), "переключение выхода");
      }
    },
    stop() {
      node.disconnect();
      // Ссылку на destination держит замыкание: собранный сборщиком
      // MediaStreamAudioDestinationNode замолкает без единого сообщения.
      destination.disconnect();
      element.srcObject = null;
    },
  };

  // Ни выбор устройства, ни запуск элемента запуск сессии не задерживают: оба
  // умеют висеть, а поток уже подключён и звук пойдёт без них.
  void playback
    .setSink(options.deviceId)
    .catch(() => options.onSinkProblem?.("Выбранный выход недоступен, играю в системный."))
    .then(() => element.play())
    .catch(() => options.onSinkProblem?.("Браузер не дал запустить воспроизведение."));
  return playback;
}
